
import { defineComponent, reactive, toRefs, watch, PropType, computed } from 'vue'
import moment, { MomentInput } from 'moment'
import DatePicker from '@/components/common/CameraDatePicker.vue'
import TimeSeeker from '@/components/common/CameraTimeSeeker.vue'
import ResolutionSelector, { RESOLUTIONS } from './CameraResolutionSelector.vue'
import { useInitDataStore } from '@/stores/initDataStore'
import { useRoute } from 'vue-router'

type State = {
  resolution: string
  targetDate: Date
  stationId?: string
}

export default defineComponent({
  components: {
    DatePicker,
    ResolutionSelector,
    TimeSeeker,
  },

  props: {
    showResolution: {
      type: Boolean,
      default: true,
      required: false,
    },

    showAdminBack: {
      type: Boolean,
      default: false,
      required: false,
    },

    defaultResolution: {
      type: String,
      default: RESOLUTIONS.MINUTE,
      required: false,
    },

    defaultDate: {
      type: Date as PropType<Date>,
      default: get_target_time(),
      required: false,
    },

    isShowSummary: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  setup(props, { emit }) {
    const initDataStore = useInitDataStore()
    const state = reactive<State>({
      resolution: props.defaultResolution,
      targetDate: props.defaultDate,
      stationId: undefined,
    })
    const route = useRoute()
    if (route.query.date) {
      state.targetDate = get_target_time(route.query.date as string).toDate()
    }

    const movePrev = () => {
      const targetDate = state.targetDate as MomentInput
      var time = moment(targetDate, 'YYYY-MM-DD-HHmm')
      var minus_min = time.minutes() % 5
      const minuteSpan: number = route.query.monitor && route.query.monitor.indexOf('history_60') != -1 ? 60 : 5

      if (state.resolution === RESOLUTIONS.HOUR || state.resolution === RESOLUTIONS.MINUTE) {
        state.targetDate = moment(targetDate, 'YYYY-MM-DD-HHmm')
          .add(-minuteSpan - minus_min, 'minute')
          .toDate()
      } else if (state.resolution === RESOLUTIONS.DAY) {
        state.targetDate = moment(targetDate).subtract(1, 'day').toDate()
      } else if (state.resolution === RESOLUTIONS.RUISEKI) {
        state.targetDate = moment(targetDate).subtract(1, 'month').toDate()
      } else if (state.resolution === RESOLUTIONS.MONTH) {
        state.targetDate = moment(targetDate).subtract(1, 'month').toDate()
      } else if (state.resolution === RESOLUTIONS.TEN_MIN) {
        state.targetDate = moment(targetDate).subtract(2, 'hour').toDate()
      }
    }
    const moveToday = () => {
      var time = moment()
      var minus_min = time.minutes() % 5

      state.targetDate = moment().add(-minus_min, 'minute').toDate()
    }
    const moveNext = () => {
      const targetDate = state.targetDate as MomentInput
      var time = moment(targetDate, 'YYYY-MM-DD-HHmm')
      var minus_min = time.minutes() % 5
      const minuteSpan: number = route.query.monitor && route.query.monitor.indexOf('history_60') != -1 ? 60 : 5

      if (state.resolution === RESOLUTIONS.HOUR || state.resolution === RESOLUTIONS.MINUTE) {
        state.targetDate = moment(targetDate, 'YYYY-MM-DD-HHmm')
          .add(+minuteSpan - minus_min, 'minute')
          .toDate()
      } else if (state.resolution === RESOLUTIONS.DAY) {
        state.targetDate = moment(targetDate).add(1, 'day').toDate()
      } else if (state.resolution === RESOLUTIONS.RUISEKI) {
        state.targetDate = moment(targetDate).add(1, 'month').toDate()
      } else if (state.resolution === RESOLUTIONS.MONTH) {
        state.targetDate = moment(targetDate).add(1, 'month').toDate()
      } else if (state.resolution === RESOLUTIONS.TEN_MIN) {
        state.targetDate = moment(targetDate).add(2, 'hour').toDate()
      }
    }

    const setResolution = (resolution: string) => {
      state.resolution = resolution
    }

    const spResolutionSelectorItemClass = (resolution: string) => {
      return state.resolution === resolution ? 'active' : ''
    }

    const showSummary = () => {
      emit('show-summary')
    }

    const hideSummary = () => {
      emit('hide-summary')
    }

    const stations = computed(() => {
      const initData = initDataStore.initData

      if (!state.resolution || !initData || !initData.summaryList) {
        return []
      }

      const times = (initData?.summaryList.multiStation || [])[0]

      const resolution = state.resolution as 'hour' | 'day' | 'month'

      if (!times || !resolution || !times[resolution]) {
        return []
      }

      return times[resolution]?.items.map(item => ({
        label: item.station?.name,
        value: item.stationId,
      }))
    })

    watch(
      () => state.resolution,
      value => emit('change-resolution', value)
    )

    watch(
      () => state.targetDate,
      value => emit('change-date', value)
    )

    watch(
      () => state.stationId,
      value => emit('change-station-id', value)
    )

    watch(
      () => props.defaultResolution,
      () => (state.resolution = props.defaultResolution)
    )

    watch(
      () => route.query.type,
      () => (state.targetDate = get_target_time().toDate())
    )

    watch(
      () => props.defaultDate,
      () => (state.targetDate = get_target_time().toDate())
    )

    watch(
      () => route.query.date,
      () => (state.targetDate = get_target_time(route.query.date as string).toDate())
    )

    return {
      ...toRefs(state),
      movePrev,
      moveToday,
      moveNext,
      setResolution,
      spResolutionSelectorItemClass,
      showSummary,
      hideSummary,
      stations,
    }
  },
})

// 基準とする時間を返す
function get_target_time(datetime = '') {
  var time = moment()
  if (datetime) {
    time = moment(datetime, 'YYYY-MM-DD-HHmm')
  }
  var minus_min = time.minutes() % 5
  return time.add(-minus_min, 'minute')
}
